import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  checkToAddOptout,
  filterValidValuesFromButton,
} from "../../../../components/commonComponents/templates/Buttons/utils";
import useToggle from "../../../../hooks/useToggle";
import {
  useAspDispatch,
  useAspSelector,
} from "../../../../test/jest-redux-hooks";
import {
  carouselApprovedGet,
  carouselCardUpdate,
  carouselSaveDraft,
  chatGpt,
  getTemplate,
  templateUpdate,
} from "../../api/Api";
import CarouselTemplate from "../../components/templates/CarouselTemplate";
import { LANGUAGE_OPTIONS } from "../../constant";
import { INITIAL_STATE, templateReducer } from "../../reducers/templateReducer";

// helper
import { capitalizeFirstLetter } from "helper/capitalizeFirstLetter";
import { getLanguageCode } from "helper/setLanguageCode";
import { getDataFromTemplate, removeDoubleStyles } from "helper/templates";
import { images } from "../../../../assets/images";
import TemplatePreview from "../../../../components/commonComponents/templates/TemplatePreview";
import TemplateDetailsHeader from "../../components/templates/TemplateDetailsHeader";
import { checkOptout } from "../../utils/buttons";

import { updateToggleToast } from "../../../../reduxToolkit/appSlice";
import TemplateBody from "../../components/templates/TemplateBody";
import TemplateButtons from "../../components/templates/TemplateButtons";
import TemplateFooter from "../../components/templates/TemplateFooter";
import TemplateHeader from "../../components/templates/TemplateHeader";
import { isBodyValid } from "../../utils/body";
import { isButtonsValid } from "../../utils/buttons";
import { formatCarouelDataToAPI, isCarouselValid } from "../../utils/carousel";
import { isHeaderValid } from "../../utils/header";
import { updateVariablesFromText } from "../../utils/variables";

import { convertMinutesIntoSecond } from "helper/timeConversion";
import AuthenticationTemplate from "../../components/templates/AuthenticationTemplate";

import { baseURL } from "../../../../api/ApiConfig";
import Loader from "../../../../components/commonComponents/Loader/Index";
import { Modal } from "antd";
import Header from "../../../../components/commonComponents/modals/Header";

function TemplateDetails() {
  const navigate = useNavigate();
  const { template } = useParams();
  const dispatch = useAspDispatch();
  const templateDetails = useAspSelector((state) => state?.Template);
  const channelsData = useAspSelector((state) => state?.Channel?.data);

  const [open, setOpen] = useState(false);

  const [templateData, templateDispatch] = useReducer(
    templateReducer,
    INITIAL_STATE
  );

  const {
    id,
    category,
    categoryType,
    language,
    name,
    status,
    custom,
    authentication,
    carousel,
  } = templateData;

  const {
    header,
    headerVariable,
    body,
    bodyVariables,
    footer,
    buttons,
    buttonsOrder,
    ctaButtons,
    customButtons,
  } = custom;

  const template_ids = useCallback(
    () => templateDetails?.id,
    [templateDetails]
  );

  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);

  const [showOnUnloadPopup, setshowOnUnloadPopup] = useToggle(false);
  const [templateName, setTemplateName] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [footerText, setFooterText] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);

  // loading
  const [isPageLoading, setIsPageLoading] = useToggle(true);
  const [loading, setLoading] = useToggle(false);
  const [isDiscard, setIsDiscard] = useToggle(false);
  const [isCategorychanging, setIsCategorychanging] = useToggle(false);
  const [isAddingCarouselCard, setIsAddingCarouselCard] = useToggle(false);

  // errors
  const [templateNameError, setTemplateNameError] = useToggle(false);
  const [isHeaderError, setIsHeaderError] = useToggle(false);

  // editor
  const [showEmojiPicker, setShowEmojiPicker] = useToggle(false);
  const checkOptOutButton = checkToAddOptout(custom?.buttons);
  const languages = getLanguageCode(selectedLanguage?.value);

  const [suggestions, setSuggestions] = useState([]);
  const [isContentLoading, setIsContentLoading] = useToggle(false);
  const [text, setText] = useState("");

  const getSuggestions = async () => {
    if (!body.trim()) return;
    setIsContentLoading(true);

    const payLoad = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `Suggest at least 2 options for: "${body}". Provide options as bullet points with emojis. Example:
          - ✅ Option 1
          - 🚀 Option 2`,
        },
      ],
      max_tokens: 100,
    };

    try {
      const response = await chatGpt(payLoad);
      setIsContentLoading(false);
      setOpen(true);

      const suggestionText = response?.choices?.[0]?.message?.content;
      const options = suggestionText
        .split("\n")
        .map((option) => option.replace(/^- /, "").trim())
        .filter((option) => option);

      setSuggestions(options);
    } catch (error) {
      setOpen(false);
      console.error("Error fetching suggestions:", error);
    }
  };

  useEffect(() => {
    setFooterText(footer?.text);
  }, [footer]);

  useEffect(() => {
    if (isAddingCarouselCard) {
      const timeout = setTimeout(async () => {
        setIsAddingCarouselCard(false);
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [isAddingCarouselCard]);

  useEffect(() => {
    if (isCategorychanging) {
      const timeout = setTimeout(() => {
        setIsCategorychanging(false);
      }, [1000]);

      return () => clearTimeout(timeout);
    }
  }, [isCategorychanging]);

  useEffect(() => {
    if (isPageLoading) {
      const timeout = setTimeout(() => {
        setIsPageLoading(false);
      }, [2000]);

      return () => clearTimeout(timeout);
    }
  }, [isPageLoading]);

  useEffect(() => {
    if (!showOnUnloadPopup) return;
    else {
      const handleonBeforeUnload = (e) => {
        e.preventDefault();
        return (e.returnValue = "");
      };

      window.addEventListener("beforeunload", handleonBeforeUnload, {
        capture: true,
      });

      return () => {
        window.removeEventListener("beforeunload", handleonBeforeUnload, {
          capture: true,
        });
      };
    }
  }, [showOnUnloadPopup]);

  useEffect(() => {
    if (language) {
      const res = LANGUAGE_OPTIONS?.filter(
        (l) =>
          l?.value?.trim()?.toLowerCase() === language?.trim()?.toLowerCase()
      );
      setSelectedLanguage(res?.[0]);
    }
  }, [language]);

  useEffect(() => {
    const categorys = capitalizeFirstLetter(category)
      ? {
          label: capitalizeFirstLetter(category),
          value: category,
        }
      : null;
    setSelectedCategory(categorys);
  }, [category]);

  useEffect(() => {
    if (template === "duplicate-template" && name?.length === 0) {
      setTemplateNameError(true);
      setTemplateName(name);
    } else {
      setTemplateName(name);
    }
  }, [name]);

  // need optimize
  useEffect(() => {
    if (isPageLoading && template_ids() && currentBrand && templateDetails) {
      if (templateDetails?.categoryType === "CAROUSEL") {
        setIsPageLoading(true);
        carouselApprovedGet(template_ids(), currentBrand?.brand_id)
          .then((res) => {
            if (res?.status === 200) {
              templateDispatch({
                type: "update_template",
                data: res?.data?.template,
              });
            }
          })
          .catch((error) => {
            console.error("Error fetching carousel template:", error);
          })
          .finally(() => {
            setIsPageLoading(false);
          });
      } else if (templateDetails?.categoryType !== "CAROUSEL") {
        setIsPageLoading(true);
        getTemplate(template_ids(), { brandId: currentBrand?.brand_id })
          .then((res) => {
            if (res?.status === 200) {
              templateDispatch({
                type: "update_template",
                data: res?.data?.template,
              });
            }
          })
          .catch((error) => {
            console.error("Error fetching carousel template:", error);
          })
          .finally(() => {
            setIsPageLoading(false);
          });
      }
    }
  }, [templateDetails, template_ids, currentBrand]);

  // need to optimize carousel cards auto save
  const cardId = carousel?.cards?.slice(-1)?.[0]?.id;

  // need to optimize carousel cards auto save
  useEffect(() => {
    if (checkCategory() === "CAROUSEL" && carousel?.cards?.length > 0) {
      const cardToUpdate = carousel?.cards?.find(
        (c) => c?.id === selectedCard?.id
      );

      if (cardToUpdate) {
        const headers = {
          type: "HEADER",
          format: carousel?.header,
          example: {
            header_handle: [cardToUpdate?.image],
          },
        };

        const body = {
          type: "BODY",
          text: cardToUpdate?.bodyText,
        };

        if (carousel?.bodyVariables?.length > 0) {
          const variables = updateVariablesFromText(
            body?.text,
            carousel?.bodyVariables
          );
          const filterValues = variables?.map((v) => v?.value);

          if (filterValues?.length > 0) {
            body["example"] = {
              body_text: [filterValues],
            };
            body["variable_metadata"] = carousel?.bodyVariables?.map((b) => ({
              title: b.title,
              attribute_id: b.attribute_id || null,
              type: b.type || 'custom',
            }));
          }
        }

        const buttons = cardToUpdate?.buttons?.map((b) => {
          if (b?.type === "URL") {
            const isLegacyTracking =
              b?.track_click === undefined &&
              b?.url?.includes("track_click");
            if (b?.track_click === true || isLegacyTracking) {
              const encodedExample = encodeURIComponent(
                btoa(JSON.stringify({ link: b?.example?.[0] }))
              );
              return {
                type: b.type,
                text: b?.text,
                example: [b?.example?.[0]],
                actual_example: [encodedExample],
                url: `${baseURL}track_click/${templateDetails?.slug}/{{1}}`,
                track_click: true,
              };
            }
            return {
              type: b.type,
              text: b?.text,
              url: b?.example?.[0] || "",
              track_click: false,
            };
          }
          if (b?.type === "PHONE_NUMBER")
            return {
              type: b?.type,
              text: b?.text,
              phone_number: `${b.code} ${b?.phone_number}`,
            };
          if (b.type === "COPY_CODE")
            return { type: b?.type, text: b?.text, example: [b?.example?.[0]] };

          return { type: b?.type, text: b?.text };
        });

        const updatedCardData = {
          components: [
            headers,
            body,
            {
              type: "BUTTONS",
              buttons: buttons,
            },
          ],
        };

        const isFilled =
          cardToUpdate?.image?.length &&
          cardToUpdate?.bodyText &&
          cardToUpdate?.buttons?.some((b) => b?.text);

        const card_template_id =
          ["create-template", "edit-template", "duplicate-template"].includes(
            template
          ) && template_ids();

        if (isFilled) {
          const debounceTimeout = setTimeout(() => {
            carouselCardUpdate(card_template_id, cardToUpdate?.id, {
              name: cardToUpdate?.name,
              brand_id: currentBrand?.brand_id,
              components: updatedCardData.components,
            }).then(() => {});
          }, 500);

          return () => clearTimeout(debounceTimeout);
        }
      }
    }
  }, [
    carousel,
    category,
    currentBrand,
    templateDetails,
    cardId,
    selectedCard,
    language,
  ]);

  useEffect(() => {
    checkValueHasChanged();
  }, [custom, authentication, carousel]);

  const checkValueHasChanged = () => {
    if (template === "create-template") {
      const data =
        JSON.stringify(INITIAL_STATE) === JSON.stringify(templateData);
      setshowOnUnloadPopup(!data);
    }
    if (
      ["edit-template", "duplicate-template"].includes(template) ||
      !["carousel", "copy_code"]?.includes(templateDetails?.categoryType)
    ) {
      const datas = getDataFromTemplate(templateDetails?.details);
      const data = JSON.stringify(datas) === JSON.stringify(templateData);
      setshowOnUnloadPopup(!data);
    }
  };

  const checkCategory = useCallback(
    () => categoryType?.toUpperCase(),
    [categoryType]
  );

  const checkOptoutisExistinCustom = () => checkOptout(custom?.customButtons);

  // check is allow to submit the template
  const checkAllowToSubmit = useCallback(() => {
    // Custom and Utility validations
    const isHeader = isHeaderValid(header, headerVariable);
    const isBody = isBodyValid(body, bodyVariables);
    const isButtons = isButtonsValid(buttons, ctaButtons, customButtons);

    // Authentication template validateion
    const isExpiryTime = authentication?.content?.expiryTimeForCode
      ? authentication?.content?.expiryTime >= 1 &&
        authentication?.content?.expiryTime <= 90
      : true;

    const isAuthenticationButtons = authentication?.buttons?.filter(
      (b) => b.text === ""
    );

    // Carousel validations
    const isCarousel = isCarouselValid(carousel);

    const res = LANGUAGE_OPTIONS?.filter(
      (l) =>
        l.label === selectedLanguage?.label &&
        l.value === selectedLanguage?.value
    );

    return isCategorychanging
      ? false
      : checkCategory() === "COPY_CODE"
      ? templateName &&
        !templateNameError &&
        authentication?.body?.length > 0 &&
        isExpiryTime &&
        isAuthenticationButtons?.length === 0
      : checkCategory() === "CAROUSEL"
      ? templateName && !templateNameError && isBody && isCarousel
      : templateName &&
        !templateNameError &&
        isHeader &&
        !isHeaderError &&
        isBody &&
        isButtons &&
        checkOptoutisExistinCustom() &&
        res?.length > 0;
  }, [
    isCategorychanging,
    checkCategory,
    templateDetails,
    templateNameError,
    custom,
    carousel,
    authentication,
    templateName,
    isHeaderError,
  ]);

  // handle buttons
  const updateButtons = useCallback(
    ({ updateKey, data, key, value, id }) => {
      if (data?.format === "OPT_OUT") {
        onDataChange({
          type: "footer",
          text: "Not interested? Tap Stop promotions",
        });
        setFooterText("Not interested? Tap Stop promotions");
      }

      if (updateKey === "remove") {
        const removed = buttons?.filter((b) => b.id === id);
        if (removed?.length > 0) {
          if (removed[0]?.format === "OPT_OUT") {
            onDataChange({
              type: "footer",
              text: "",
            });
            setFooterText("");
          }
        }
      }
      onDataChange({
        type: "buttons",
        updateKey: updateKey,
        data: data
          ? { id: id, ...data }
          : {
              id: id,
              key: key,
              value: value,
            },
      });
    },
    [buttons]
  );

  useEffect(() => {
    if (body) {
      let data = {};
      const languages = getLanguageCode(selectedLanguage?.value);
      const { components } = formatCarouelDataToAPI({ body, bodyVariables });
      data = {
        category: selectedCategory?.value,
        name: templateName,
        language: languages,
        brand_id: currentBrand?.brand_id,
        components: [...components, { type: "CAROUSEL", cards: [] }],
        save_as_draft: true,
      };

      const card_template_id =
        ["create-template", "edit-template", "duplicate-template"].includes(
          template
        ) && template_ids();

      const debounceTimeout = setTimeout(() => {
        if (templateDetails?.categoryType === "CAROUSEL") {
          carouselSaveDraft(card_template_id, {
            ...data,
          });
        }
      }, 500);
      return () => clearTimeout(debounceTimeout);
    }
  }, [
    body,
    bodyVariables,
    selectedCategory,
    templateDetails,
    selectedLanguage,
    currentBrand,
  ]);

  const createAndUpdateTemplateData = async (draft = "false") => {
    setLoading(true);

    let data = {};
    let component = [];

    if (header) {
      component.push(header);
    }

    if (footer?.text?.length > 0) {
      component.push(footer);
    }

    if (bodyVariables?.length > 0) {
      component.push({
        type: "BODY",
        text: body,
        example: {
          body_text: [
            bodyVariables?.map((b) => {
              return b.value;
            }),
          ],
        },
        // Store variable metadata for profile attribute resolution during message sending
        variable_metadata: bodyVariables?.map((b) => ({
          title: b.title,
          attribute_id: b.attribute_id || null,
          type: b.type || 'custom',
        })),
      });
    } else {
      component.push({
        type: "BODY",
        text: removeDoubleStyles(body),
      });
    }

    if (buttons?.length > 0) {
      const cta = filterValidValuesFromButton(ctaButtons, templateDetails?.slug);
      const custom = filterValidValuesFromButton(customButtons);
      component.push({
        type: "BUTTONS",
        buttons:
          buttonsOrder === "ctaButtons"
            ? [...cta, ...custom]
            : [...custom, ...cta],
      });
    }

    if (checkCategory() === "COPY_CODE") {
      const autheticationButtons = authentication?.buttons?.map((b) => {
        const { id, label, placeholder, ...rest } = b;
        return rest;
      });

      data = {
        name: templateName,
        category: selectedCategory?.value,
        language: languages,
        brand_id: currentBrand?.brand_id,
        save_as_draft: draft,
        components: [
          {
            type: "BODY",
            add_security_recommendation:
              authentication?.content?.securityRecommendation,
          },
          {
            type: "BUTTONS",
            buttons: autheticationButtons,
          },
        ],
      };

      if (authentication?.content?.expiryTimeForCode)
        data = {
          ...data,
          components: [
            ...data?.components,
            {
              type: "FOOTER",
              code_expiration_minutes: authentication?.content?.expiryTime,
            },
          ],
        };

      if (authentication?.messageValidity?.validity) {
        data["validity_period"] = convertMinutesIntoSecond(
          authentication?.messageValidity?.period
        );
      } else {
        const { validity_period, ...rest } = data;
        data = rest;
      }
    } else if (checkCategory() === "CAROUSEL") {
      data = {
        category: selectedCategory?.value,
        name: templateName,
        language: languages,
        brand_id: currentBrand?.brand_id,
        components: [...component, { type: "CAROUSEL", cards: [] }],
        save_as_draft: draft,
      };
    } else if (checkCategory() === "ORDER_DETAIL") {
      const buttonsComponent = { type: "BUTTONS", buttons: [{ type: "ORDER_DETAILS", text: "Review and Pay" }] };
      const buttonsIndex = component.findIndex(comp => comp.type === "BUTTONS");
      const updatedComponents = [...component];

      if (buttonsIndex !== -1) {
        updatedComponents[buttonsIndex] = buttonsComponent;
      } else {
        updatedComponents.push(buttonsComponent);
      }

      data = {
        category: selectedCategory?.value,
        name: templateName,
        language: languages,
        brand_id: currentBrand?.brand_id,
        components: updatedComponents,
        save_as_draft: draft,
      };
    } else {
      data = {
        category: selectedCategory?.value,
        name: templateName,
        language: languages,
        brand_id: currentBrand?.brand_id,
        components: component,
        save_as_draft: draft,
      };
    }
    const card_template_id =
      ["create-template", "edit-template", "duplicate-template"].includes(
        template
      ) && template_ids();
    if (checkCategory() === "CAROUSEL") {
      carouselSaveDraft(card_template_id, {
        ...data,
      })
        .then((res) => {
          setLoading(false);
          if (res?.status === 200) {
            dispatch(
              updateToggleToast([
                ...toggleToast,
                {
                  id: toggleToast?.length + 1,
                  content: "Template Updated successfully",
                  status: "Success",
                  duration: "",
                },
              ])
            );
            navigate("/user/channels/whatsapp/message-templates");
          } else {
            dispatch(
              updateToggleToast([
                ...toggleToast,
                {
                  id: toggleToast?.length + 1,
                  content: "Template update creation failed. Please try again!",
                  status: "Error",
                  duration: "",
                },
              ])
            );
          }
        })
        .catch((error) => {
          console.error("Error creating/updating template:", error);
        });
    }

    if (
      // template === 'edit-template' &&
      // templateDetails?.details?.status !== 'DRAFT' &&
      checkCategory() !== "CAROUSEL"
    ) {
      if (
        templateDetails?.details?.validity_period &&
        !authentication?.messageValidity?.validity
      ) {
        data["validity_period"] = -1;
      }
      templateUpdate(template_ids(), data)
        .then((res) => {
          setLoading(false);
          if (res?.status === 200) {
            dispatch(
              updateToggleToast([
                ...toggleToast,
                {
                  id: toggleToast?.length + 1,
                  content: "Template Updated successfully",
                  status: "Success",
                  duration: "",
                },
              ])
            );
            navigate("/user/channels/whatsapp/message-templates");
          } else {
            dispatch(
              updateToggleToast([
                ...toggleToast,
                {
                  id: toggleToast?.length + 1,
                  content: res?.response?.data?.error || "Template update creation failed. Please try again!",
                  status: "Error",
                  duration: "",
                },
              ])
            );
          }
        })
        .catch((error) => {
          console.error("Error creating/updating template:", error);
        });
    }
  };

  const onDataChange = ({
    type,
    primaryKey,
    secondaryKey,
    updateKey,
    id,
    key,
    value,
    text,
    data,
    attribute_id,
    variable_type,
  }) => {
    const payload = {
      type: type,
      key: key,
      value: value,
    };

    if (primaryKey) payload["primaryKey"] = primaryKey;
    if (secondaryKey) payload["secondaryKey"] = secondaryKey;
    if (updateKey) payload["updateKey"] = updateKey;
    if (id) payload["id"] = id;
    if (text) payload["text"] = text;
    if (data) payload["data"] = data;
    if (attribute_id) payload["attribute_id"] = attribute_id;
    if (variable_type) payload["variable_type"] = variable_type;

    templateDispatch(payload);
  };

  useEffect(() => {
    let data = {};
    if (checkCategory() !== "CAROUSEL") {
      if (checkCategory() === "COPY_CODE") {
        const autheticationButtons = authentication?.buttons?.map((b) => {
          const { id, label, placeholder, ...rest } = b;
          return rest;
        });
        data = {
          name: templateName,
          category: selectedCategory?.value,
          language: languages,
          brand_id: currentBrand?.brand_id,
          save_as_draft: true,
          components: [
            {
              type: "BODY",
              add_security_recommendation:
                authentication?.content?.securityRecommendation,
            },
            {
              type: "BUTTONS",
              buttons: autheticationButtons,
            },
          ],
        };

        if (authentication?.content?.expiryTimeForCode)
          data = {
            ...data,
            components: [
              ...data?.components,
              {
                type: "FOOTER",
                code_expiration_minutes: authentication?.content?.expiryTime,
              },
            ],
          };

        if (authentication?.messageValidity?.validity) {
          data["validity_period"] = convertMinutesIntoSecond(
            authentication?.messageValidity?.period
          );
        } else {
          const { validity_period, ...rest } = data;
          data = rest;
        }

        const debounceTimeout = setTimeout(() => {
          templateUpdate(template_ids(), data);
        }, 500);
        return () => clearTimeout(debounceTimeout);
      } else {
        let component = [];
        if (header) {
          component.push(header);
        }

        if (!checkOptOutButton && footer && footer?.text?.length > 0) {
          component.push(footer);
        } else if (checkOptOutButton && footer && footer?.text?.length > 0) {
          component.push(footer);
        }

        if (bodyVariables?.length > 0) {
          component.push({
            type: "BODY",
            text: body,
            example: {
              body_text: [
                bodyVariables?.map((b) => {
                  return b.value;
                }),
              ],
            },
            variable_metadata: bodyVariables?.map((b) => ({
              title: b.title,
              attribute_id: b.attribute_id || null,
              type: b.type || 'custom',
            })),
          });
        } else {
          component.push({
            type: "BODY",
            text: removeDoubleStyles(body),
          });
        }
        if (buttons?.length > 0) {
          const cta = filterValidValuesFromButton(ctaButtons, templateDetails?.slug);
          const custom = filterValidValuesFromButton(customButtons);
          component.push({
            type: "BUTTONS",
            buttons:
              buttonsOrder === "ctaButtons"
                ? [...cta, ...custom]
                : [...custom, ...cta],
          });
        }

      if (checkCategory() === "ORDER_DETAIL") {
          component.push({
            type: "BUTTONS",
            buttons: [
              {
                type: "ORDER_DETAILS",
                text: "Review and Pay",
              },
            ],
          });
        }

        let data = {};

        data = {
          category: selectedCategory?.value,
          name: templateName,
          language: languages,
          brand_id: currentBrand?.brand_id,
          components: component,
          save_as_draft: true,
        };

        const debounceTimeout = setTimeout(() => {
          templateUpdate(template_ids(), data);
        }, 500);
        return () => clearTimeout(debounceTimeout);
      }
    }
  }, [
    authentication,
    custom,
    languages,
    footer,
    templateName,
    checkOptOutButton,
  ]);

  if (isCategorychanging || isPageLoading) {
    return (
      <div className="w-full h-auto p-[0.5vw]">
        <div className="bg-white flex items-center justify-center p-6 my-4 rounded">
          <Loader Width={30} Height={30} />
        </div>
      </div>
    );
  }

  const handleOptions = (index) => {
    setOpen(false);
    const selectedText = suggestions[index];

    onDataChange({
      type: "body",
      updateKey: "custom",
      text: selectedText,
    });
  };

  return (
    <div
      className={`w-full max-h-[92vh] overflow-hidden ${
        loading ? "cursor-not-allowed" : "cursor-default"
      }`}
    >
      {isCategorychanging ? (
        <div className="w-full h-auto p-[0.5vw]">
          <div className="bg-white flex items-center justify-center p-6 my-4 rounded">
            <Loader Width={30} Height={30} />
          </div>
        </div>
      ) : (
        <>
          <TemplateDetailsHeader
            category={checkCategory}
            checkAllowToSubmit={checkAllowToSubmit}
            createAndUpdateTemplateData={createAndUpdateTemplateData}
            isDiscard={isDiscard}
            setIsDiscard={setIsDiscard}
            templateName={templateName}
            setTemplateName={setTemplateName}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            isCategorychanging={isCategorychanging}
            setIsCategorychanging={setIsCategorychanging}
            templateNameError={templateNameError}
            setTemplateNameError={setTemplateNameError}
            buttons={buttons}
            loading={loading}
            onChange={onDataChange}
            status={status}
            updateButtons={updateButtons}
          />
          <div className="w-full flex justify-center gap-[0.5vw] overflow-hidden p-[0.5vw]">
            <div className="w-[25vw] bg-[var(--white)]">
              <div className="w-full font-default weight-semibold text-[var(--contentText)] px-[1vw] py-[1vw]">
                Message Preview
              </div>
              <div className="max-w-[18.5vw] w-full flex-1 overflow-hidden px-2.5">
                <div className="h-[6vh] flex items-center gap-[0.5vw] bg-[#077C70] rounded-t-[1.5vw] px-[0.6vw]">
                  <div className="size-[2.1vw] bg-[var(--white)] rounded-full flex items-center justify-center font-xs">
                    Logo
                  </div>
                  <div className="text-[var(--white)]">
                    <h3 className="font-md weight-bold capitalize leading-[1.2vw]">
                      {channelsData?.whatsapp_business_account_name}
                    </h3>
                  </div>
                </div>
                <TemplatePreview
                  category={checkCategory()}
                  header={header}
                  headerVariable={headerVariable}
                  body={body}
                  bodyVariables={bodyVariables}
                  footer={footer}
                  buttons={buttons}
                  buttonsOrder={buttonsOrder}
                  ctaButtons={ctaButtons}
                  customButtons={customButtons}
                  data={templateDetails?.details}
                  className={
                    "w-full rounded-none h-[55vh] max-[1600px]:h-[55vh]"
                  }
                  authentication={authentication}
                  carousel={carousel}
                  isAddingCarouselCard={isAddingCarouselCard}
                  setIsAddingCarouselCard={setIsAddingCarouselCard}
                />
                <img
                  src={images?.TemplatePreviewBottom}
                  alt="send msg"
                  className="w-[17.2vw]  rounded-b-[1.5vw]"
                />
              </div>
            </div>
            <div
              className={`w-full bg-[var(--white)] px-[0.7vw] py-[1vw] overflow-y-scroll ${
                templateNameError ? "h-[82vh]" : "h-[85vh]"
              }`}
              >
              {/* For Authentication Template */}
              {checkCategory() === "COPY_CODE" ? (
                <AuthenticationTemplate
                  data={authentication}
                  dispatch={templateDispatch}
                  onChange={onDataChange}
                />
                ) : checkCategory() === "CAROUSEL" ? (
                  // For Carousel Template
                <CarouselTemplate
                  id={template_ids()}
                  language={selectedLanguage?.value ?? ""}
                  body={body}
                  dispatch={templateDispatch}
                  headerVariable={headerVariable}
                  variables={bodyVariables}
                  allow={!loading}
                  showEmojiPicker={showEmojiPicker}
                  setShowEmojiPicker={setShowEmojiPicker}
                  data={carousel}
                  isAddingCarouselCard={isAddingCarouselCard}
                  setIsAddingCarouselCard={setIsAddingCarouselCard}
                  setSelectedCard={setSelectedCard}
                  selectedCard={selectedCard}
                  onChange={onDataChange}
                />
              ) : (
                // For Other Template
                <>
                  <div className="border-b-[0.08vw] border-[var(--card-border)] pb-[1.5vw]">
                    <TemplateHeader
                      header={header}
                      headerVariable={headerVariable}
                      dispatch={templateDispatch}
                      allow={!loading}
                      onChange={onDataChange}
                      setIsHeaderError={setIsHeaderError}
                      templateId={template_ids()}
                      currentBrand={currentBrand}
                      category={checkCategory()}
                    />
                  </div>
                  <div className="my-[1vw]">
                    <TemplateBody
                      title="Body"
                      subTitle="Enter the text for your message in the language that you've selected."
                      type="default"
                      language={selectedLanguage?.value ?? ""}
                      body={body}
                      dispatch={templateDispatch}
                      headerVariable={headerVariable}
                      variables={bodyVariables}
                      allow={!loading}
                      showEmojiPicker={showEmojiPicker}
                      setShowEmojiPicker={setShowEmojiPicker}
                      getSuggestions={getSuggestions}
                      isContentLoading={isContentLoading}
                      onVariableChange={(value) => {
                        onDataChange({
                          type: "update_variable",
                          key: value?.key,
                          value: value.value,
                        });
                      }}
                      onChange={(value) => {
                        onDataChange({
                          type: "body",
                          updateKey: "custom",
                          text: value?.text,
                          key: value?.key,
                          value: value?.value,
                          attribute_id: value?.attribute_id,
                          variable_type: value?.variable_type,
                        });
                      }}
                      currentBrand={currentBrand}
                    />
                  </div>
                  <div className="mt-[1.5vw]">
                    <TemplateFooter
                      footer={footer}
                      dispatch={templateDispatch}
                      disabled={!checkOptOutButton}
                      allow={!loading}
                      text={footerText}
                      setText={setFooterText}
                      onChange={onDataChange}
                    />
                  </div>
                  <div className="w-full mt-[1.5vw] pt-[1.5vw] border-t-[0.08vw] border-[var(--card-border)]">
                    <TemplateButtons
                      buttons={buttons}
                      ctaButtons={ctaButtons}
                      customButtons={customButtons}
                      order={buttonsOrder}
                      updateButtons={updateButtons}
                      allow={!loading}
                      onChange={onDataChange}
                      category={checkCategory()}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      <Modal
        open={open}
        styles={{content: {padding: 0}}}
        afterOpenChange={(open) => {
          setOpen(open);
        }}
        width={"37vw"}
        centered
        footer={null}
        closable={false}
        wrapClassName={"bg-[#00000095]"}
        destroyOnClose={true}
      >
        <Header
          title={"Suggested Contents"}
          handleCancel={() => setOpen(false)}
          className="font18px-500 text-[var(--textBlack)]"
        />
        <div className="prl-20 pb-20">
          {suggestions.map((option, index) => (
            <div
              key={index}
              className="border b-radius-6 mt-10 p-10 pointer"
              onClick={() => handleOptions(index)}
            >
              {option}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

export default TemplateDetails;
